import { useLayoutEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { useI18n } from "@/context/I18nContext";

import { sepOf, group, fmtAmount, fmtRp } from "@/lib/num";

export { fmtAmount, fmtRp };
const esc = (c) => (c === "." ? "\\." : c);

// Text being typed -> grouped text + plain number string ("1234567.5"), max 2 decimals.
function parse(raw, lang) {
  const { g, d } = sepOf(lang);
  const s = raw.replace(new RegExp(`[^0-9${esc(d)}]`, "g"), "");
  const k = s.indexOf(d);
  let int = (k >= 0 ? s.slice(0, k) : s).replace(/^0+(?=\d)/, "");
  const dec = k >= 0 ? s.slice(k + 1).replace(new RegExp(esc(d), "g"), "").slice(0, 2) : null;
  if (!int && dec !== null) int = "0";
  return { txt: (int ? group(int, g) : "") + (dec !== null ? d + dec : ""), canon: !int && dec === null ? "" : `${int}${dec ? `.${dec}` : ""}` };
}

// Editing text for a stored value: grouped, decimals only when not zero.
function editText(canon, lang) {
  if (canon === "" || !Number.isFinite(Number(canon))) return "";
  const { d } = sepOf(lang);
  const [i, f] = Number(canon).toFixed(2).split(".");
  return parse(f === "00" ? i : `${i}${d}${f.replace(/0$/, "")}`, lang).txt;
}

// Pasted text in any common format ("1.000.000,50", "1,000,000.50", "1000000.5") -> plain number string.
function pasteCanon(text) {
  const s = text.replace(/[^0-9.,]/g, "");
  const lastDot = s.lastIndexOf(".");
  const lastCom = s.lastIndexOf(",");
  let dec = Math.max(lastDot, lastCom);
  if (lastDot < 0 || lastCom < 0) {
    const c = s[dec];
    if (dec < 0 || s.split(c).length > 2 || s.length - dec - 1 > 2) dec = -1;
  }
  const int = (dec >= 0 ? s.slice(0, dec) : s).replace(/[.,]/g, "");
  const frac = dec >= 0 ? s.slice(dec + 1).replace(/[.,]/g, "").slice(0, 2) : "";
  return int || frac ? `${int || "0"}${frac ? `.${frac}` : ""}` : "";
}

// Amount box like Tally: thousands are grouped while typing (1.000.000,00 in Indonesian, 1,000,000.00 in English / Chinese);
// the form receives a plain number string.
export function AmountInput({ value, onChange, onFocus, onBlur, onKeyDown, className, ...props }) {
  const { lang } = useI18n();
  const { d } = sepOf(lang);
  const ref = useRef(null);
  const caret = useRef(null);
  const [text, setText] = useState(null);
  const canon = value === null || value === undefined ? "" : String(value);
  const shown = text ?? (canon === "" ? "" : fmtAmount(canon, lang));

  useLayoutEffect(() => {
    const el = ref.current;
    const n = caret.current;
    if (!el || n === null || document.activeElement !== el) return;
    let i = 0;
    for (let seen = 0; i < el.value.length && seen < n; i += 1) {
      if (/[0-9]/.test(el.value[i]) || el.value[i] === d) seen += 1;
    }
    el.setSelectionRange(i, i);
    caret.current = null;
  });

  const apply = (raw, pos) => {
    const keep = new RegExp(`[^0-9${esc(d)}]`, "g");
    let n = raw.slice(0, pos).replace(keep, "").length;
    if (n > 0 && raw.replace(keep, "").startsWith(d)) n += 1; // a "0" is put before a leading decimal separator
    caret.current = n;
    const p = parse(raw, lang);
    setText(p.txt);
    onChange(p.canon);
  };
  const keyDown = (e) => {
    onKeyDown?.(e);
    const decKey = e.key === "." || e.key === "," || e.code === "NumpadDecimal";
    if (!decKey || e.ctrlKey || e.metaKey || e.defaultPrevented) return;
    e.preventDefault();
    const el = e.currentTarget;
    const s = el.selectionStart ?? el.value.length;
    const en = el.selectionEnd ?? s;
    if ((el.value.slice(0, s) + el.value.slice(en)).includes(d)) return; // one decimal separator only
    apply(`${el.value.slice(0, s)}${d}${el.value.slice(en)}`, s + 1);
  };
  const paste = (e) => {
    e.preventDefault();
    const c = pasteCanon(e.clipboardData.getData("text"));
    setText(editText(c, lang));
    onChange(c);
  };

  return (
    <Input ref={ref} type="text" inputMode="decimal" autoComplete="off" value={shown} className={className} {...props}
      onChange={(e) => apply(e.target.value, e.target.selectionStart ?? e.target.value.length)}
      onKeyDown={keyDown} onPaste={paste}
      onFocus={(e) => { setText(editText(canon, lang)); const el = e.currentTarget; requestAnimationFrame(() => { if (document.activeElement === el) el.select(); }); onFocus?.(e); }}
      onBlur={(e) => { setText(null); onBlur?.(e); }} />
  );
}
